import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import Timeline from "@material-ui/lab/Timeline";
import TimelineConnector from "@material-ui/lab/TimelineConnector";
import TimelineContent from "@material-ui/lab/TimelineContent";
import TimelineDot from "@material-ui/lab/TimelineDot";
import TimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import { useTranslate } from "react-admin";
import {
  getAccountGroupParent,
  getAccountGroupingTree,
  assignAccountToGroup,
  moveAccountBetweenGroups,
} from "services/setting";
import SnackbarUtils from "utils/SnackbarUtils";

const useStyles = makeStyles((theme) => ({
  panel: {
    margin: 20,
    height: "calc(100% - 40px)",
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  headerIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0,
  },
  accountSummary: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(2),
  },
  accountCode: {
    fontWeight: 700,
    lineHeight: 1.3,
  },
  accountName: {
    marginTop: 2,
  },
  breadcrumb: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(2),
  },
  breadcrumbText: {
    fontSize: 12,
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  breadcrumbTextUnassigned: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  breadcrumbSeparator: {
    fontSize: 12,
    margin: "0 4px",
    color: theme.palette.text.secondary,
  },
  timeline: {
    padding: 0,
    margin: 0,
    "& .MuiTimelineItem-missingOppositeContent:before": {
      flex: 0,
      padding: 0,
    },
  },
  timelineItem: {
    minHeight: 58,
  },
  connector: {
    backgroundColor: theme.palette.divider,
    minHeight: 22,
  },
  timelineContent: {
    paddingTop: 0,
    paddingBottom: theme.spacing(1.5),
    paddingRight: 0,
  },
  levelLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  levelCode: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  levelName: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.35,
    color: theme.palette.text.primary,
  },
  currentLevel: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
  emptyState: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.action.hover,
    borderRadius: 8,
    border: `1px dashed ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    minHeight: 120,
  },
  moveChoice: {
    padding: theme.spacing(1, 1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    appearance: "none",
    font: "inherit",
    boxSizing: "border-box",
  },
  moveChoiceBody: {
    minWidth: 0,
  },
  moveChoiceSelected: {
    borderColor: "#5b50f6",
    backgroundColor: "#edf0ff",
  },
  modalCode: {
    color: "#4256d0",
    fontWeight: 700,
    fontSize: "0.84rem",
  },
  modalName: {
    color: "#1f2a44",
    fontSize: "0.92rem",
  },
  modalList: {
    marginTop: theme.spacing(2),
    maxHeight: 320,
    overflowY: "auto",
  },
}));

const getLevelNumber = (item) => {
  if (!item) return 1;
  const level = item.Level !== undefined ? item.Level : item.LevelNo;
  if (typeof level === "number" && Number.isFinite(level)) {
    return level;
  }
  if (typeof level === "string") {
    const match = level.match(/\d+/);
    if (match) {
      return Number(match[0]);
    }
  }
  return 1;
};

const getGroupCode = (item) => {
  if (!item) return "";
  return item.AccGroupCode || item.GroupCode || item.code || "";
};

const getGroupName = (item) => {
  if (!item) return "";
  return item.AccGroupName || item.GroupName || item.name || "";
};

const flattenTree = (nodes, result = [], depth = 0) => {
  if (!nodes) return result;
  for (const node of nodes) {
    result.push({
      Id: node.AccGroupId ?? node.Id,
      AccGroupId: node.AccGroupId ?? node.Id,
      AccGroupCode: node.AccGroupCode ?? node.GroupCode,
      AccGroupName: node.AccGroupName ?? node.GroupName,
      Level: node.Level,
      Children: node.Children || [],
      _depth: depth,
    });
    if (node.Children) {
      flattenTree(node.Children, result, depth + 1);
    }
  }
  return result;
};

const AccountCodeGroupingPath = (props) => {
  const { accountCode, accountDescription, accountDescription2, mode = "view" } = props;
  const classes = useStyles();
  const translate = useTranslate();

  const [groupPath, setGroupPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [selectedTargetGroupId, setSelectedTargetGroupId] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const fetchPath = useCallback(async () => {
    if (!accountCode) {
      setGroupPath([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getAccountGroupParent(accountCode);
      let rawPath = [];
      if (data && Array.isArray(data.AccountGrouping)) {
        rawPath = data.AccountGrouping;
      } else if (data && Array.isArray(data.Data)) {
        rawPath = data.Data;
      } else if (Array.isArray(data)) {
        rawPath = data;
      } else if (data && typeof data === "object") {
        rawPath = [data];
      }

      const pathMap = {};
      rawPath.forEach((item) => {
        if (item) {
          const lv = getLevelNumber(item);
          pathMap[lv] = {
            level: lv,
            code: getGroupCode(item),
            name: getGroupName(item),
            isAssigned: true,
            Id: item.AccGroupId ?? item.Id,
          };
        }
      });

      const finalPath = [1, 2, 3, 4].map((lv) => {
        if (pathMap[lv]) {
          return pathMap[lv];
        }
        return {
          level: lv,
          code: "-",
          name: translate("ra.info.unassigned") || "Unassigned",
          isAssigned: false,
        };
      });

      setGroupPath(finalPath);
    } catch (err) {
      console.error("Error fetching account group parent path:", err);
      const fallbackPath = [1, 2, 3, 4].map((lv) => ({
        level: lv,
        code: "-",
        name: translate("ra.info.unassigned") || "Unassigned",
        isAssigned: false,
      }));
      setGroupPath(fallbackPath);
    } finally {
      setLoading(false);
    }
  }, [accountCode, translate]);

  useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(() => {
      if (isMounted) {
        fetchPath();
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [fetchPath]);

  const currentGroup = [...groupPath].reverse().find((node) => node.isAssigned);

  const handleOpenMoveDialog = async () => {
    setDialogOpen(true);
    setSelectedTargetGroupId("");
    setSearchText("");
    try {
      const response = await getAccountGroupingTree();
      if (response && response.Data) {
        setTreeData(response.Data);
      }
    } catch (err) {
      console.error("Error fetching account group tree:", err);
    }
  };

  const handleSaveGroupChange = async () => {
    if (!selectedTargetGroupId) return;

    setSaveLoading(true);
    try {
      let response;
      if (currentGroup && currentGroup.Id) {
        response = await moveAccountBetweenGroups({
          FromGroupId: currentGroup.Id,
          ToGroupId: selectedTargetGroupId,
          AccCode: [accountCode],
        });
      } else {
        response = await assignAccountToGroup({
          GroupId: selectedTargetGroupId,
          AccCode: [accountCode],
        });
      }

      if (response && response.Code === 0) {
        SnackbarUtils.success(response.UserMessage || "Group changed successfully.");
        setDialogOpen(false);
        fetchPath();
      } else {
        SnackbarUtils.warning(response?.UserMessage || "Failed to change group.");
      }
    } catch (err) {
      console.error("Error saving group change:", err);
      SnackbarUtils.error("An error occurred while saving group change.");
    } finally {
      setSaveLoading(false);
    }
  };

  const flattenedGroups = flattenTree(treeData);
  const targetMoveGroups = flattenedGroups.filter(
    (group) => (group.Children || []).length === 0
  );

  const filteredTargetGroups = targetMoveGroups.filter((group) =>
    [group.AccGroupCode, group.AccGroupName]
      .filter(Boolean)
      .some((val) => val.toLowerCase().includes(searchText.trim().toLowerCase()))
  );

  return (
    <Paper variant="outlined" className={classes.panel}>
      <div className={classes.header}>
        <AccountTreeIcon className={classes.headerIcon} />
        <Typography className={classes.title} color="textPrimary">
          {translate("ra.module.Account Code Grouping Path")}
        </Typography>
      </div>

      <div className={classes.accountSummary}>
        <Typography variant="caption" color="textSecondary">
          {translate("ra.fieldAbbr.accountCode")}
        </Typography>
        <Typography className={classes.accountCode} variant="subtitle1" color="textPrimary">
          {accountCode || "-"}
        </Typography>
        <Typography className={classes.accountName} variant="body2" color="textSecondary">
          {accountDescription || accountDescription2 || "-"}
        </Typography>
      </div>

      {loading ? (
        <div className={classes.emptyState} style={{ borderStyle: "solid" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            {translate("ra.info.loading") || "Loading..."}
          </Typography>
        </div>
      ) : groupPath.length > 0 ? (
        <>
          <Timeline align="left" className={classes.timeline}>
            {groupPath.map((node, index) => {
              const isCurrent = currentGroup && node.level === currentGroup.level;
              return (
                <TimelineItem key={`${node.level}-${node.code}`} className={classes.timelineItem}>
                  <TimelineSeparator>
                    <TimelineDot
                      color={isCurrent ? "primary" : "grey"}
                      variant={node.isAssigned ? "default" : "outlined"}
                    />
                    {index < groupPath.length - 1 ? <TimelineConnector className={classes.connector} /> : null}
                  </TimelineSeparator>
                  <TimelineContent className={classes.timelineContent}>
                    <Typography className={classes.levelLabel}>LEVEL {node.level}</Typography>
                    <Typography className={classes.levelCode}>{node.code}</Typography>
                    <Typography className={classes.levelName}>{node.name}</Typography>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>

          <div className={classes.currentLevel}>
            <Typography variant="caption" color="textSecondary">
              Current Group
            </Typography>
            <Typography className={classes.levelCode}>
              {currentGroup ? `Level ${currentGroup.level} | ${currentGroup.code}` : "-"}
            </Typography>
            <Typography className={classes.levelName}>
              {currentGroup ? currentGroup.name : translate("ra.info.notGroupedYet")}
            </Typography>
          </div>
        </>
      ) : (
        <div className={classes.emptyState}>
          <Typography variant="body2" color="textSecondary" align="center">
            {translate("ra.info.notGroupedYet")}
          </Typography>
        </div>
      )}

      {mode !== "view" && accountCode && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenMoveDialog}
            disabled={loading}
            style={{ textTransform: "none", borderRadius: 8 }}
          >
            {translate("ra.btn.changeGroup") || "Change Group"}
          </Button>
          <Typography
            variant="caption"
            color="textSecondary"
            align="center"
            style={{ padding: "0 16px", fontSize: 11, lineHeight: 1.4 }}
          >
            {translate("ra.info.changeGroupWarning")}
          </Typography>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle onClose={() => setDialogOpen(false)}>
          {translate("ra.field.Move") || "Move / Change Group"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            {translate("ra.field.Target Parent Group") || "Select Leaf Group"}
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={translate("ra.field.Search groups...") || "Search groups..."}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginTop: 8, marginBottom: 8 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchText("")}
                    aria-label="Clear search"
                    style={{ padding: 4 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <div className={classes.modalList}>
            {filteredTargetGroups.map((group) => {
              const selected = Number(selectedTargetGroupId) === Number(group.AccGroupId);
              const indent = group._depth * 12;
              return (
                <button
                  type="button"
                  key={group.AccGroupId}
                  className={`${classes.moveChoice} ${selected ? classes.moveChoiceSelected : ""}`}
                  onClick={() => setSelectedTargetGroupId(group.AccGroupId)}
                  aria-pressed={selected}
                  style={{
                    marginLeft: indent,
                    width: `calc(100% - ${indent}px)`,
                  }}
                >
                  <div className={classes.moveChoiceBody}>
                    <div className={classes.modalCode}>{group.AccGroupCode}</div>
                    <div className={classes.modalName}>{group.AccGroupName}</div>
                  </div>
                </button>
              );
            })}
            {!filteredTargetGroups.length && (
              <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 16 }}>
                {translate("ra.info.notransaction") || "No groups found"}
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {translate("ra.action.cancel") || "Cancel"}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={saveLoading || !selectedTargetGroupId}
            onClick={handleSaveGroupChange}
          >
            {translate("ra.field.Move") || "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AccountCodeGroupingPath;
